import { useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Package, FolderTree, ShoppingBag, Gift, Ticket as TicketIcon, Newspaper, Settings, Bike, ExternalLink, X,
} from 'lucide-react'
import { ADMIN_NAV } from '../../config/admin.config'
import { useAdminUiStore } from '../../state/adminUiStore'

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'layout-dashboard': LayoutDashboard,
  'package': Package,
  'folder-tree': FolderTree,
  'shopping-bag': ShoppingBag,
  'gift': Gift,
  'ticket': TicketIcon,
  'newspaper': Newspaper,
  'settings': Settings,
}

function SidebarContent({ onItemClick }: { onItemClick?: () => void }) {
  return (
    <>
      <Link to="/admin" onClick={onItemClick} className="h-16 px-5 flex items-center gap-3 border-b border-slate-800 shrink-0">
        <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center text-white">
          <Bike className="w-5 h-5" />
        </div>
        <div className="leading-tight">
          <div className="font-bold text-white text-sm">Xe Điện Việt</div>
          <div className="text-[11px] text-slate-400">Admin Panel</div>
        </div>
      </Link>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <ul className="space-y-0.5">
          {ADMIN_NAV.map((item) => {
            const Icon = ICONS[item.icon] ?? LayoutDashboard
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end ?? false}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-3 h-11 px-3 rounded-md text-sm font-medium transition',
                      isActive
                        ? 'bg-brand-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                    ].join(' ')
                  }
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="p-3 border-t border-slate-800 shrink-0">
        <Link
          to="/"
          target="_blank"
          onClick={onItemClick}
          className="flex items-center gap-2 h-10 px-3 rounded-md text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition"
        >
          <ExternalLink className="w-4 h-4" /> Xem trang khách
        </Link>
      </div>
    </>
  )
}

export function AdminSidebar() {
  const open = useAdminUiStore((s) => s.mobileNavOpen)
  const close = useAdminUiStore((s) => s.closeMobileNav)
  const { pathname } = useLocation()

  useEffect(() => { if (open) close() }, [pathname]) // eslint-disable-line

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, close])

  return (
    <>
      <aside className="hidden lg:flex flex-col w-[240px] shrink-0 bg-slate-900 text-slate-200 h-screen sticky top-0">
        <SidebarContent />
      </aside>

      <div
        onClick={close}
        className={[
          'fixed inset-0 bg-black/50 z-40 transition-opacity lg:hidden',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />
      <aside
        className={[
          'fixed top-0 left-0 h-full w-[280px] bg-slate-900 text-slate-200 z-50 flex flex-col transition-transform duration-300 lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <button
          onClick={close}
          className="absolute top-4 right-3 w-9 h-9 rounded-full hover:bg-slate-800 flex items-center justify-center text-slate-300"
          aria-label="Đóng menu"
        >
          <X className="w-5 h-5" />
        </button>
        <SidebarContent onItemClick={close} />
      </aside>
    </>
  )
}
